<template>
  <div class="form">
    <h6 class="form__title">{{ t('register.title') }}</h6>
    <ui-input
        :type="'text'"
        :label="t('fields.firstName')"
        :rules="[required]"
        v-model="firstName"
    />
    <ui-input
        :type="'text'"
        :label="t('fields.lastName')"
        :rules="[required]"
        v-model="lastName"
    />
    <ui-input
        :type="'email'"
        :label="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
    />
    <ui-input
        :type="'password'"
        :label="t('fields.password')"
        :rules="[isPasswordValid]"
        v-model="password"
    />
    <ui-input
        :type="'password'"
        :label="t('fields.confirmPassword')"
        :rules="[compareStrings]"
        v-model="confirmPassword"
    />
    <ui-checkbox
        v-model="isAgree"
    >
      <i18n-t tag="p" scope="global" keypath="checkboxes.agree">
        <template #terms>
          <router-link
              class="form__link"
              :to="translations.i18nRoute({ name: 'terms' })"
          >
            {{ t('docs.terms.title') }}
          </router-link>
        </template>
        <template #policy>
          <router-link
              class="form__link"
              :to="translations.i18nRoute({ name: 'policy' })"
          >
            {{ t('docs.policy.title') }}
          </router-link>
        </template>
      </i18n-t>
    </ui-checkbox>
    <div class="form__captcha">
      <Checkbox v-model="captcha" />
    </div>
    <ui-button
        class="form__button"
        :isDisabled="!isFormValid"
        @click="register"
    >
      {{ t('buttons.register') }}
    </ui-button>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import {computed, h, ref} from "vue";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import translations from "@/core/helpers/translations.js";
import UiButton from "@/components/ui/ui-button.vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";
import { Checkbox } from 'vue-recaptcha';

const {t} = useI18n()

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const isAgree = ref(false)
const captcha = ref()

const isLoading = ref(false)
const mailClient = ref(null)

const compareStrings = (v) => {
  if (v === password.value) return true
  return t('errors.compare')
}

const isFormValid = computed(() => {
  return (
    required(firstName.value) === true &&
    required(lastName.value) === true &&
    isEmail(email.value) === true &&
    isPasswordValid(password.value) === true &&
    compareStrings(confirmPassword.value) === true &&
    isAgree.value &&
    captcha.value
  )
})

const register = async () => {
  isLoading.value = true

  const res = await Auth.register({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    password: password.value,
    passwordConfirm: confirmPassword.value
  })

  if (res?.data?.createUser?.success) {
    const domain = email.value.split('@')[1]
    const mailClients = {
      'gmail.com': 'https://mail.google.com/',
      'outlook.com': 'https://outlook.live.com/',
      'icloud.com': 'https://www.icloud.com/',
      'yahoo.com': 'https://mail.yahoo.com/'
    }

    Object.entries(mailClients).forEach((el) => {
      if (domain === el[0]) mailClient.value = el[1]
    })

    ElNotification({
      title: t('popup.register.title'),
      message: h('div', [
        h('p', t('popup.register.text')),
        mailClient ? h(
          'button',
          {
            style: {
              marginTop: '10px',
              padding: '6px 12px',
              backgroundColor: '#fe6064',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            },
            onClick: () => {
              window.location.href = mailClient.value
            }
          },
          t('buttons.goEmail')
        ) : ''
      ]),
      type: 'success'
    })
  } else {
    ElNotification({
      title: t('popup.error'),
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-block: 10px;
  padding-right: 20px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);

  &__title {
    color: #2F2F2F;
    font-size: 22px;
    font-weight: 600;
    line-height: 30.8px;
    text-transform: uppercase;
  }

  &__link {
    transition: 0.2s;

    @include hover {
      color: #FF6162;
    }
  }

  &__button {
    padding-block: 12px;
    font-size: 14px;
  }
}

@media (max-width: 1100px) {
  .form {
    width: 400px;
    align-items: center;
    padding-right: 0;
    padding-bottom: 30px;
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
}

@media (max-width: 500px) {
  .form {
    width: 100%;
  }
}
</style>